/**
 * Mock "server-side" data source for the Copilot demo scenario. Deterministic
 * (seeded LCG, no Math.random) so every walkthrough shows identical data:
 *   - tbl_companies: 128 HR-tech/SaaS companies with $100M+ funding
 *   - tbl_prospects: 3,210 prospects, exactly 210 of which carry a
 *     "Sales Development" title (incl. Adam Wheeler) for the corrections beat.
 *
 * fetchPage simulates network latency so table skeleton states render. When
 * the real agent backend lands, the store swaps these calls for makeApi.
 */

const PAGE_LATENCY_MS = 300;

const FIRST_NAMES = [
  "Adam", "Priya", "Marcus", "Elena", "Jordan", "Sofia", "Liam", "Aisha",
  "Noah", "Maya", "Ethan", "Zara", "Lucas", "Nina", "Owen", "Tara",
  "Caleb", "Ivy", "Dylan", "Rosa", "Felix", "Anya", "Hugo", "Leah"
];
const LAST_NAMES = [
  "Wheeler", "Sharma", "Bennett", "Vargas", "Kim", "Rossi", "Carter",
  "Khan", "Foster", "Patel", "Hayes", "Moreau", "Brooks", "Tanaka",
  "Reid", "Okafor", "Silva", "Novak", "Grant", "Ali"
];
const COMPANY_STEMS = [
  "People", "Talent", "Hire", "Work", "Team", "Crew", "Staff", "Skill",
  "Culture", "Perk", "Payroll", "Bench", "Recruit", "Onboard", "Thrive", "Grow"
];
const COMPANY_SUFFIXES = ["HQ", "ly", "Base", "Stack", "Hub", "Flow", "OS", "Labs"];
const INDUSTRIES = ["HR Tech", "SaaS", "HR Tech · SaaS"];
const LOCATIONS = [
  "San Francisco, CA", "New York, NY", "Austin, TX", "Boston, MA",
  "Seattle, WA", "Denver, CO", "Chicago, IL", "Atlanta, GA",
  "London, UK", "Toronto, CA", "Berlin, DE", "Bangalore, IN"
];

// Persona-aligned titles (the "good" pool).
const TITLES = [
  "Chief Marketing Officer", "Chief Revenue Officer", "VP of Marketing",
  "VP of Sales", "VP of Business Development", "Demand Generation Manager",
  "Director of Demand Generation", "RevOps Manager", "Sales Operations Manager",
  "Head of Growth Marketing", "Director of Sales Operations", "VP of Revenue Operations"
];
// The 210 rows the corrections beat removes.
const SALES_DEV_TITLES = [
  "Sales Development Manager", "Sales Development Representative",
  "Head of Sales Development", "Senior Sales Development Representative"
];
export const SALES_DEV_MATCH = /sales development/i;

const lcg = seed => {
  let state = seed >>> 0;
  return () => {
    state = (state * 1664525 + 1013904223) >>> 0;
    return state / 4294967296;
  };
};

const pick = (arr, rand) => arr[Math.floor(rand() * arr.length)];

const buildCompanies = () => {
  const rand = lcg(42);
  const rows = [];
  const seen = {};
  while (rows.length < 128) {
    const name = `${pick(COMPANY_STEMS, rand)}${pick(COMPANY_SUFFIXES, rand)}`;
    const key = `${name}_${rows.length}`;
    if (seen[name]) {
      seen[name] += 1;
    } else {
      seen[name] = 1;
    }
    const displayName = seen[name] > 1 ? `${name} ${seen[name]}` : name;
    rows.push({
      id: `co_${rows.length + 1}`,
      key,
      company: displayName,
      industry: pick(INDUSTRIES, rand),
      funding: `$${100 + Math.floor(rand() * 900)}M`,
      fundingRound: pick(["Series C", "Series D", "Series E", "Growth"], rand),
      location: pick(LOCATIONS, rand)
    });
  }
  return rows;
};

const buildProspects = companies => {
  const rand = lcg(1337);
  const rows = [];
  const total = 3210;
  const salesDevCount = 210;
  // Spread the 210 sales-dev rows evenly so early pages show a few (Adam is
  // forced onto page 1 for the demo beat).
  const salesDevEvery = Math.floor(total / salesDevCount);
  for (let i = 0; i < total; i += 1) {
    const isSalesDev = i > 0 && i % salesDevEvery === 0 && rows.filter(r => r.salesDev).length < salesDevCount - 1;
    const firstName = pick(FIRST_NAMES, rand);
    const lastName = pick(LAST_NAMES, rand);
    const company = pick(companies, rand);
    rows.push({
      id: `pr_${i + 1}`,
      name: `${firstName} ${lastName}`,
      title: isSalesDev ? pick(SALES_DEV_TITLES, rand) : pick(TITLES, rand),
      salesDev: isSalesDev,
      company: company.company,
      industry: company.industry,
      location: pick(LOCATIONS, rand)
    });
  }
  // Deterministic hero row for the corrections beat, visible on page 1.
  rows[2] = {
    id: "pr_adam",
    name: "Adam Wheeler",
    title: "Sales Development Manager",
    salesDev: true,
    company: companies[0].company,
    industry: companies[0].industry,
    location: "Austin, TX"
  };
  return rows;
};

const companies = buildCompanies();
const prospects = buildProspects(companies);

const tables = {
  tbl_companies: { rows: companies },
  tbl_prospects: { rows: prospects }
};

const liveRows = tableId => {
  const table = tables[tableId];
  if (!table) return [];
  return table.rows.filter(row => !row.removed);
};

export const getTotalCount = tableId => liveRows(tableId).length;

export const fetchPage = (tableId, page = 1, pageSize = 20) =>
  new Promise(resolve => {
    setTimeout(() => {
      const rows = liveRows(tableId);
      const start = (page - 1) * pageSize;
      resolve({
        rows: rows.slice(start, start + pageSize),
        totalCount: rows.length,
        page,
        pageSize
      });
    }, PAGE_LATENCY_MS);
  });

/** Remove every live row whose title matches — returns how many were removed. */
export const removeByTitleMatch = (tableId, matcher = SALES_DEV_MATCH) => {
  let removed = 0;
  liveRows(tableId).forEach(row => {
    if (matcher.test(row.title || "")) {
      row.removed = true;
      removed += 1;
    }
  });
  return removed;
};

export const removeRowById = (tableId, rowId) => {
  const row = (tables[tableId]?.rows || []).find(item => item.id === rowId);
  if (row && !row.removed) {
    row.removed = true;
    return 1;
  }
  return 0;
};

/** Reset all removals — used when the conversation is reset. */
export const resetTables = () => {
  Object.keys(tables).forEach(tableId => {
    tables[tableId].rows.forEach(row => {
      row.removed = false;
    });
  });
};

export const COMPANY_COLUMNS = [
  { key: "company", label: "Company" },
  { key: "industry", label: "Industry" },
  { key: "fundingRound", label: "Round" },
  { key: "funding", label: "Funding" },
  { key: "location", label: "HQ" }
];

export const PROSPECT_COLUMNS = [
  { key: "name", label: "Name" },
  { key: "title", label: "Title" },
  { key: "company", label: "Company" },
  { key: "industry", label: "Industry" },
  { key: "location", label: "Location" }
];
